<script setup lang="ts">
// Yes/no switch filter. Renders an accessible `role="switch"` checkbox
// styled as a sliding toggle. Emits the new boolean value to the host
// the moment the user toggles.

import { computed, useId } from "vue";

import type { ToggleOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: boolean;
  opts?: ToggleOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: boolean): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text.
const labelId = useId();

// Print-mode display: the author's `onLabel` / `offLabel` if set, else
// generic Yes/No. Same string the live switch shows next to its
// slider, just frozen for paper.
const printValue = computed(() =>
  props.value ? props.opts?.onLabel ?? "Yes" : props.opts?.offLabel ?? "No",
);

function onChange(e: Event): void {
  const next = (e.target as HTMLInputElement).checked;
  emit("change-target", props.id, next);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="toggle">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="toggle-label"
      :label="opts?.label"
      :description="opts?.description"
      :label-id="labelId"
    />
    <label class="switch">
      <input
        type="checkbox"
        role="switch"
        :aria-labelledby="opts?.label ? labelId : undefined"
        :checked="value"
        @change="onChange"
      />
      <span class="slider" />
      <span v-if="opts?.onLabel || opts?.offLabel" class="state-label">
        {{ value ? opts?.onLabel ?? "" : opts?.offLabel ?? "" }}
      </span>
    </label>
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.toggle {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .toggle {
    display: flex;
    width: 100%;
  }
}
.toggle-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: relative;
  width: 36px;
  height: 20px;
  background: var(--rule);
  border-radius: 999px;
  transition: background 0.15s ease;
}
.slider::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: var(--card);
  border-radius: 50%;
  transition: transform 0.15s ease;
}
.switch input:checked + .slider {
  background: var(--primary);
}
.switch input:checked + .slider::after {
  transform: translateX(16px);
}
.switch input:focus-visible + .slider {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.state-label {
  color: var(--card-ink);
  font-size: 13px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  .switch {
    min-height: 44px;
  }
}
</style>
